import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Tags, Save, X } from 'lucide-react';
import { genreAPI } from '../../services/api';
import { useGenreStore } from '../../hooks/useStore';
import toast from 'react-hot-toast';
import './Admin.css';

const GenreManagement = () => {
  const { genres, fetchGenres } = useGenreStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingGenre, setEditingGenre] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    icon: '📚',
    color: '#8b5cf6',
    description: ''
  });

  const iconOptions = ['📚', '💕', '⚔️', '🔮', '👻', '😂', '🏫', '🌟', '🔥', '💀', '🎭', '🌸', '🎮', '🚀', '🏆', '💼'];

  useEffect(() => {
    fetchGenres();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setFormData({ name: '', icon: '📚', color: '#8b5cf6', description: '' });
    setEditingGenre(null);
  };

  const openModal = (genre = null) => {
    if (genre) {
      setEditingGenre(genre);
      setFormData({
        name: genre.name,
        icon: genre.icon || '📚',
        color: genre.color || '#8b5cf6',
        description: genre.description || ''
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter genre name');
      return;
    }

    setIsLoading(true);
    try {
      if (editingGenre) {
        await genreAPI.update(editingGenre._id, formData);
        toast.success('Genre updated successfully');
      } else {
        await genreAPI.create(formData);
        toast.success('Genre created successfully');
      }
      fetchGenres();
      closeModal();
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    
    try {
      await genreAPI.delete(id);
      toast.success('Genre deleted');
      fetchGenres();
    } catch (error) {
      toast.error('Failed to delete genre');
    }
  };

  return (
    <div className="genre-management">
      <div className="admin-page-header">
        <h1>Genre Management</h1>
        <button className="btn-add" onClick={() => openModal()}>
          <Plus size={18} />
          Add Genre
        </button>
      </div>

      {genres.length > 0 ? (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Icon</th>
                <th>Name</th>
                <th>Description</th>
                <th>Novels</th>
                <th>Color</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {genres.map((genre) => (
                <tr key={genre._id}>
                  <td style={{ fontSize: '1.5rem' }}>{genre.icon}</td>
                  <td>
                    <strong>{genre.name}</strong>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      /{genre.slug}
                    </div>
                  </td>
                  <td style={{ maxWidth: '300px' }}>
                    <div style={{ 
                      whiteSpace: 'nowrap', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      color: 'var(--text-secondary)'
                    }}>
                      {genre.description || '-'}
                    </div>
                  </td>
                  <td>{genre.novelCount || 0}</td>
                  <td>
                    <div style={{
                      width: '30px',
                      height: '30px',
                      borderRadius: '6px',
                      background: genre.color || '#8b5cf6'
                    }} />
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="btn-edit" onClick={() => openModal(genre)}>
                        <Edit size={16} />
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDelete(genre._id, genre.name)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="admin-empty">
          <Tags size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
          <p>No genres yet</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingGenre ? 'Edit Genre' : 'Add Genre'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Genre Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Romance, Fantasy..."
                />
              </div>

              <div className="form-group">
                <label>Icon</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {iconOptions.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon })}
                      style={{
                        width: '44px',
                        height: '44px',
                        fontSize: '1.5rem',
                        background: formData.icon === icon ? 'var(--primary)' : 'var(--bg-dark)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Color</label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    style={{ width: '60px', height: '40px', border: 'none', cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Short description of the genre..."
                  rows={3}
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit" disabled={isLoading}>
                  {isLoading ? 'Saving...' : (
                    <>
                      <Save size={18} />
                      {editingGenre ? 'Update' : 'Add'}
                    </>
                  )}
                </button>
                <button type="button" className="btn-cancel" onClick={closeModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        .modal-content {
          background: var(--bg-card);
          border-radius: var(--radius-lg);
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid var(--border);
        }
        .modal-header h2 {
          font-size: 1.2rem;
        }
        .modal-close {
          background: none;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
        }
        .modal-content form {
          padding: 24px;
        }
      `}</style>
    </div>
  );
};

export default GenreManagement;
